/**
 * Route Preloader Utility
 *
 * Preloads lazy-loaded route components before user navigates to them,
 * eliminating loading delays and improving perceived performance.
 */

type PreloadableComponent = () => Promise<{ default: React.ComponentType<any> }>;

interface PreloadedRoute {
  name: string;
  loader: PreloadableComponent;
  preloaded: boolean;
  loading: boolean;
}

class RoutePreloader {
  private routes: Map<string, PreloadedRoute> = new Map();
  private preloadQueue: string[] = [];
  private isPreloading = false;

  /**
   * Register a route for potential preloading
   */
  register(name: string, loader: PreloadableComponent) {
    this.routes.set(name, {
      name,
      loader,
      preloaded: false,
      loading: false,
    });
  }

  /**
   * Preload a specific route immediately
   */
  async preload(name: string): Promise<void> {
    const route = this.routes.get(name);
    if (!route) {
      console.warn(`[RoutePreloader] Route "${name}" not registered`);
      return;
    }

    if (route.preloaded || route.loading) {
      return; // Already preloaded or in progress
    }

    route.loading = true;

    try {
      await route.loader();
      route.preloaded = true;
      if (import.meta.env.DEV) {
        console.log(`[RoutePreloader] ✓ Preloaded route: ${name}`);
      }
    } catch (error) {
      console.error(`[RoutePreloader] Failed to preload route: ${name}`, error);
    } finally {
      route.loading = false;
    }
  }

  /**
   * Preload multiple routes in sequence
   */
  async preloadMany(names: string[]): Promise<void> {
    for (const name of names) {
      await this.preload(name);
    }
  }

  /**
   * Preload routes on idle (uses requestIdleCallback when available)
   */
  preloadOnIdle(names: string[], priority: 'high' | 'low' = 'low') {
    if (priority === 'high') {
      // High priority: preload immediately
      this.preloadMany(names);
      return;
    }

    // Low priority: wait for idle time
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.preloadMany(names);
      }, { timeout: 2000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        this.preloadMany(names);
      }, 1000);
    }
  }

  /**
   * Preload route on link hover (predictive preloading)
   */
  preloadOnHover(name: string) {
    // Add small delay to avoid preloading on accidental hovers
    const timerId = setTimeout(() => {
      this.preload(name);
    }, 100);

    return () => clearTimeout(timerId);
  }

  /**
   * Check if a route is preloaded
   */
  isPreloaded(name: string): boolean {
    return this.routes.get(name)?.preloaded || false;
  }

  /**
   * Get preload statistics
   */
  getStats() {
    const total = this.routes.size;
    const preloaded = Array.from(this.routes.values()).filter(r => r.preloaded).length;
    const loading = Array.from(this.routes.values()).filter(r => r.loading).length;

    return {
      total,
      preloaded,
      loading,
      percentage: total > 0 ? Math.round((preloaded / total) * 100) : 0,
    };
  }
}

// Singleton instance
export const routePreloader = new RoutePreloader();

/**
 * React hook for route preloading
 */
export function useRoutePreloader() {
  return {
    preload: (name: string) => routePreloader.preload(name),
    preloadMany: (names: string[]) => routePreloader.preloadMany(names),
    preloadOnIdle: (names: string[], priority?: 'high' | 'low') =>
      routePreloader.preloadOnIdle(names, priority),
    preloadOnHover: (name: string) => routePreloader.preloadOnHover(name),
    isPreloaded: (name: string) => routePreloader.isPreloaded(name),
    getStats: () => routePreloader.getStats(),
  };
}

/**
 * Preload critical authenticated routes after login
 */
export function preloadAuthenticatedRoutes(onboardingCompleted: boolean = true) {
  // Only preload onboarding if user hasn't completed it
  const routes = onboardingCompleted
    ? ['dashboard']
    : ['dashboard', 'onboarding'];

  routePreloader.preloadOnIdle(routes, 'high');
}

/**
 * Preload auth routes when user hovers over login/register buttons
 */
export function preloadAuthRoutes() {
  routePreloader.preloadOnIdle(['login', 'register'], 'low');
}
